import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPackage, FiFilter } from 'react-icons/fi';
import adminService from '../../services/admin.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const statusColors = {
  pending: 'warning', confirmed: 'info', processing: 'info',
  shipped: 'primary', delivered: 'success', cancelled: 'danger', refunded: 'secondary',
};

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 20 };
        if (statusFilter) params.status = statusFilter;
        if (paymentFilter) params.paymentStatus = paymentFilter;
        const { data } = await adminService.getOrders(params);
        setOrders(data.data);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [page, statusFilter, paymentFilter]);

  return (
    <div className="section-padding">
      <div className="container">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <h2><FiPackage className="me-2" />All Orders</h2>
          <div className="d-flex gap-2">
            <select className="form-select form-select-sm" style={{ width: 'auto' }}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select className="form-select form-select-sm" style={{ width: 'auto' }}
              value={paymentFilter}
              onChange={(e) => { setPaymentFilter(e.target.value); setPage(1); }}>
              <option value="">All Payment</option>
              <option value="pending">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        {loading ? <LoadingSpinner /> : (
          <>
            <div className="dashboard-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Merchant</th>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Total</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length > 0 ? orders.map((order) => (
                      <tr key={order.id}>
                        <td>
                          <Link to={`/account/orders/${order.id}`} className="fw-semibold text-primary text-decoration-none">
                            {order.orderNumber}
                          </Link>
                        </td>
                        <td className="text-muted small">{order.userId?.substring(0, 8)}...</td>
                        <td className="text-muted small">{order.merchantId?.substring(0, 8)}...</td>
                        <td>
                          <span className={`badge bg-${statusColors[order.status] || 'secondary'}`}>
                            {order.status}
                          </span>
                        </td>
                        <td>
                          <span className={`badge bg-${order.paymentStatus === 'paid' ? 'success' : 'warning'}`}>
                            {order.paymentStatus}
                          </span>
                        </td>
                        <td className="fw-semibold">${parseFloat(order.totalAmount).toFixed(2)}</td>
                        <td className="text-muted small">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">No orders found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.pages > 1 && (
              <nav className="d-flex justify-content-center mt-3">
                <ul className="pagination">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(page - 1)}>Prev</button>
                  </li>
                  {Array.from({ length: Math.min(pagination.pages, 10) }, (_, i) => (
                    <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= pagination.pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(page + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            )}

            <p className="text-muted small mt-2">
              {pagination.total} total orders
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage;
